import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
    Check,
    CircleAlert,
    Info,
    LucideAngularModule,
    LucideIconData,
    TriangleAlert,
    X,
} from 'lucide-angular';
import { TwxToast, TwxToastTone } from './twx-toast.service';

const LEAVE_MS = 160;

/**
 * One notification card, in TwinnyX's own voice: a black card (the product's accent),
 * Manrope title, an icon tile for the tone, close, and a hairline at the bottom that runs
 * down the toast's time. Hovering pauses both the clock and the line; the card leaves by
 * itself or on close. It rises from the corner (the toaster sits at the bottom right).
 */
@Component({
    selector: 'twx-toast',
    imports: [LucideAngularModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'group pointer-events-auto block w-full',
        '[class.twx-toast-leaving]': 'leaving()',
        '(mouseenter)': 'pause()',
        '(mouseleave)': 'resume()',
        '(focusin)': 'pause()',
        '(focusout)': 'resume()',
    },
    styles: [
        `
            :host {
                animation: twx-toast-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
            }
            :host.twx-toast-leaving {
                animation: twx-toast-out 160ms ease-in both;
            }
            .twx-toast-run {
                transform-origin: left;
                animation: twx-toast-run linear both;
                animation-duration: var(--twx-toast-ms);
            }
            :host(:hover) .twx-toast-run,
            :host(:focus-within) .twx-toast-run {
                animation-play-state: paused;
            }
            @keyframes twx-toast-in {
                from {
                    opacity: 0;
                    transform: translateY(10px) scale(0.97);
                }
                to {
                    opacity: 1;
                    transform: none;
                }
            }
            @keyframes twx-toast-out {
                to {
                    opacity: 0;
                    transform: translateY(6px) scale(0.97);
                }
            }
            @keyframes twx-toast-run {
                from {
                    transform: scaleX(1);
                }
                to {
                    transform: scaleX(0);
                }
            }
            @media (prefers-reduced-motion: reduce) {
                :host,
                :host.twx-toast-leaving {
                    animation-duration: 1ms;
                }
            }
        `,
    ],
    template: `
        <div
            class="relative flex items-start gap-3 overflow-hidden rounded-2xl bg-zinc-950 p-3.5 pr-3 text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.45),0_1px_2px_rgba(0,0,0,0.2)] ring-1 ring-inset ring-white/10"
            [style.--twx-toast-ms]="toast().duration + 'ms'"
            [attr.role]="toast().tone === 'error' ? 'alert' : 'status'">
            <span
                class="mt-px flex size-7 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset"
                [class]="tile()">
                <lucide-icon [img]="glyph()" [size]="15" [strokeWidth]="2.25" />
            </span>

            <div class="flex min-w-0 flex-1 flex-col gap-0.5 py-0.5">
                @if (toast().title) {
                    <p
                        class="twx-display text-[13.5px] font-bold leading-snug tracking-[-0.015em] text-white">
                        {{ toast().title }}
                    </p>
                }
                @if (toast().message) {
                    <p
                        class="text-pretty text-[12.5px] leading-snug"
                        [class]="
                            toast().title
                                ? 'text-zinc-400'
                                : 'twx-display font-semibold tracking-[-0.01em] text-white'
                        ">
                        {{ toast().message }}
                    </p>
                }
            </div>

            <button
                class="-my-1 flex size-6 shrink-0 items-center justify-center rounded-md text-zinc-500 opacity-0 transition-[opacity,background-color,color] hover:bg-white/10 hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
                type="button"
                [attr.aria-label]="'twinnyx.toast.close' | translate"
                (click)="close()">
                <lucide-icon [img]="XIcon" [size]="14" [strokeWidth]="2" />
            </button>

            <span class="absolute inset-x-0 bottom-0 h-px bg-white/10" aria-hidden="true">
                <span class="twx-toast-run block h-full w-full" [class]="line()"></span>
            </span>
        </div>
    `,
})
export class TwxToastComponent implements OnInit {
    readonly toast = input.required<TwxToast>();
    readonly closed = output<number>();

    protected readonly XIcon = X;
    protected readonly leaving = signal(false);

    protected readonly glyph = computed<LucideIconData>(() => GLYPH[this.toast().tone]);
    protected readonly tile = computed(() => TILE[this.toast().tone]);
    protected readonly line = computed(() => LINE[this.toast().tone]);

    private readonly destroyRef = inject(DestroyRef);
    private timer: ReturnType<typeof setTimeout> | null = null;
    private remaining = 0;
    private startedAt = 0;

    ngOnInit() {
        this.remaining = this.toast().duration;
        this.resume();
        this.destroyRef.onDestroy(() => this.stop());
    }

    protected pause() {
        if (!this.timer) return;
        this.stop();
        this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
    }

    protected resume() {
        if (this.timer || this.leaving()) return;
        this.startedAt = Date.now();
        this.timer = setTimeout(() => this.close(), this.remaining);
    }

    protected close() {
        if (this.leaving()) return;
        this.stop();
        this.leaving.set(true);
        setTimeout(() => this.closed.emit(this.toast().id), LEAVE_MS);
    }

    private stop() {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
    }
}

const GLYPH: Record<TwxToastTone, LucideIconData> = {
    success: Check,
    info: Info,
    warning: TriangleAlert,
    error: CircleAlert,
};

/** The icon tile on the black card: white for what went well; amber and red keep their meaning. */
const TILE: Record<TwxToastTone, string> = {
    success: 'bg-white text-zinc-950 ring-white',
    info: 'bg-white/10 text-white ring-white/15',
    warning: 'bg-amber-400/15 text-amber-300 ring-amber-300/30',
    error: 'bg-red-500/15 text-red-300 ring-red-400/30',
};

const LINE: Record<TwxToastTone, string> = {
    success: 'bg-white',
    info: 'bg-white/70',
    warning: 'bg-amber-400',
    error: 'bg-red-400',
};
